import styled from "styled-components";

import { media } from "../styles/media";

export const DashboardStyles = styled.section`
  min-height: calc(100vh - 42px);
  width: 100%;

  .userContainer {
    max-width: 960px;
    margin-top: ${({ theme }) => theme.spacing[24]};

    /* Slim dark-blue greeting bar, harmonized with the profile hero.
       ponytail: cover gradient duplicated from accountSettings.styles.js
       (.heroCover); extract to src/styles only if a 3rd screen needs it. */
    .slimBar {
      border-radius: ${({ theme }) => theme.radius[12]};
      overflow: hidden;
      box-shadow: ${({ theme }) => theme.shadows.card};
      background: radial-gradient(
          circle at 88% 0%,
          rgba(255, 255, 255, 0.1) 0,
          transparent 45%
        ),
        radial-gradient(
          circle at 12% 100%,
          rgba(255, 255, 255, 0.08) 0,
          transparent 50%
        ),
        linear-gradient(
          120deg,
          ${({ theme }) => theme.colors.accent[500]} 0%,
          ${({ theme }) => theme.colors.accent[700]} 60%,
          ${({ theme }) => theme.colors.accent[900]} 100%
        );
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 16px 24px;
    }

    .avatar {
      width: 48px;
      height: 48px;
      flex-shrink: 0;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      font-size: 18px;
      font-weight: 600;
      letter-spacing: 0.5px;
      color: #fff;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.26) 0%,
        rgba(255, 255, 255, 0.08) 100%
      );
      box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.16),
        0 3px 10px rgba(0, 0, 0, 0.22);

      img {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        object-fit: cover;
      }
    }

    .info {
      flex: 1;
      min-width: 0;

      .eyebrow {
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 1.2px;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.6);
      }

      .name {
        margin-top: 2px;
        font-size: 20px;
        line-height: 24px;
        font-weight: 600;
        letter-spacing: -0.4px;
        color: #fff;
      }

      .tagline {
        margin-top: 8px;
        display: inline-flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
        font-size: 13px;
      }

      .rolePill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 3px 10px;
        border-radius: 999px;
        font-size: 12.5px;
        font-weight: 500;
        color: #fff;
        background: rgba(255, 255, 255, 0.14);
        border: 1px solid rgba(255, 255, 255, 0.22);
        -webkit-backdrop-filter: blur(6px);
        backdrop-filter: blur(6px);
      }
    }

    .barRight {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      gap: 18px;
    }

    .dateBlock {
      text-align: right;

      .day {
        font-size: 14px;
        font-weight: 600;
        color: #fff;
      }

      .date {
        margin-top: 2px;
        font-size: 12.5px;
        color: rgba(255, 255, 255, 0.6);
      }
    }
  }

  .onboardingContainer {
    max-width: 960px;
    margin-top: ${({ theme }) => theme.spacing[24]};
  }

  /* When the onboarding orchestrator renders nothing (set-up account), collapse
     the wrapper so it doesn't add an empty gap above the data cards. */
  .onboardingContainer:empty {
    display: none;
  }

  .cardsContainer {
    max-width: 960px;
    display: flex;
    justify-content: space-between;
    margin-top: ${({ theme }) => theme.spacing[24]};
    gap: ${({ theme }) => theme.spacing[24]};

    .card {
      width: 100%;
      border-radius: ${({ theme }) => theme.radius[12]};
      overflow: hidden;

      .servicesList {
        height: 280px;
        padding: ${({ theme }) => theme.spacing[8]} ${({ theme }) => theme.spacing[12]};
      }

      .hasMore {
        font-style: normal;
        font-weight: ${({ theme }) => theme.typography.body.b3.weight};
        font-size: ${({ theme }) => theme.typography.body.b3.size};
        line-height: ${({ theme }) => theme.typography.body.b3.lineHeight};
        color: ${({ theme }) => theme.colors.semantic.text.weak};
        padding-bottom: ${({ theme }) => theme.spacing[8]};
        text-align: center;
      }

      .lockedHeader {
        width: 100%;
        height: 97px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 0 ${({ theme }) => theme.spacing[24]};
        border-bottom: 1px solid
          ${({ theme }) => theme.colors.semantic.separator};

        .lockedNumber {
          font-weight: 600;
          font-size: ${({ theme }) => theme.typography.heading.h3.size};
          line-height: ${({ theme }) => theme.typography.heading.h3.lineHeight};
          color: ${({ theme }) => theme.colors.primary[200]};
        }

        .lockedTitle {
          font-weight: 600;
          font-size: ${({ theme }) => theme.typography.body.b3.size};
          line-height: ${({ theme }) => theme.typography.body.b3.lineHeight};
          text-transform: uppercase;
          color: ${({ theme }) => theme.colors.primary[400]};
          margin-top: ${({ theme }) => theme.spacing[4]};
        }
      }

      .lockedBody {
        height: 280px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: ${({ theme }) => theme.spacing[12]};
        padding: ${({ theme }) => theme.spacing[24]};
        text-align: center;

        .lockedIcon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: ${({ theme }) => theme.colors.accent[50]};
          color: ${({ theme }) => theme.colors.primary[300]};
          display: grid;
          place-items: center;
        }

        .lockedText {
          font-size: ${({ theme }) => theme.typography.body.b2.size};
          line-height: ${({ theme }) => theme.typography.body.b2.lineHeight};
          color: ${({ theme }) => theme.colors.primary[400]};
          max-width: 220px;
        }
      }
    }
  }

  ${media.maxMobile`
    .userContainer .slimBar {
      flex-direction: column;
      align-items: flex-start;
      gap: 14px;
    }

    .userContainer .barRight {
      width: 100%;
      justify-content: space-between;
    }

    .userContainer .dateBlock {
      text-align: left;
    }

    .cardsContainer {
      flex-direction: column;
    }
  `}
`;
